import { useEffect, useRef, useState } from 'react';
import { useAiChat } from '../../../hooks/features/aiChat';
import ChatDisclaimer from './ChatDisclaimer';
import ChatEmptyState from './ChatEmptyState';
import ChatMessage from './ChatMessage';
import TypingIndicator from './TypingIndicator';
import ChatInputArea from './ChatInputArea';

/**
 * 検索結果詳細画面「AIに聞く」チャットパネル
 *
 * @param {Object} props
 * @param {string} props.blobName - 表示中文書のBlob名
 * @param {string} props.indexName - 検索対象のインデックス名
 * @param {number} props.insuranceType - 保険種別（1:生保, 2:損保）
 * @param {string} [props.documentType] - 文書種別（例: "しおり・約款"。ドメインコンテキスト選択に使用）
 * @param {(pageNumber: number) => void} props.onPageJump - 引用チップクリック時のPDFページ遷移
 */
const AiChatPanel = ({ blobName, indexName, insuranceType, documentType, onPageJump }) => {
  const {
    messages,
    status,
    hint,
    progressLabel,
    hasStreamingMessage,
    isInputDisabled,
    showResetButton,
    sendMessage,
    resetConversation,
  } = useAiChat({
    blobName,
    indexName,
    insuranceType,
    documentType,
  });
  const [inputValue, setInputValue] = useState('');
  const messagesRef = useRef(null);

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages, status]);

  /**
   * 入力欄の内容をフックへ送信し、送信後は入力欄を空にする。
   * 空白のみの入力は送信しない（実際の無効化判定はuseAiChat側のisInputDisabledが担う）。
   */
  const handleSend = () => {
    if (!inputValue.trim()) return;
    sendMessage(inputValue);
    setInputValue('');
  };

  /**
   * 「新しい質問を始める」ボタン押下時、会話状態をフック側でリセットし入力欄もクリアする。
   */
  const handleReset = () => {
    resetConversation();
    setInputValue('');
  };

  return (
    <div className="ai-chat-panel">
      <ChatDisclaimer />

      <div className="chat-messages" ref={messagesRef}>
        {messages.length === 0 ? (
          <ChatEmptyState />
        ) : (
          messages.map((message, index) => (
            <ChatMessage key={index} message={message} onPageJump={onPageJump} />
          ))
        )}
        {status === 'awaiting_response' && !hasStreamingMessage && <TypingIndicator label={progressLabel} />}
      </div>

      <ChatInputArea
        value={inputValue}
        onChange={setInputValue}
        onSend={handleSend}
        disabled={isInputDisabled}
        hint={hint}
        isError={status === 'error'}
        showResetButton={showResetButton}
        onReset={handleReset}
      />
    </div>
  );
};

export default AiChatPanel;
